import React, { useState, useEffect } from 'react'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore.js'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { useTrusteesFooter, renderTrusteesHtml } from '../../components/reportTrusteesFooter'

function JurnalUmum() {
  const { user } = useAuthStore()
  const [journals, setJournals] = useState([])
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [startDay, setStartDay] = useState('');
  const [startMonth, setStartMonth] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endDay, setEndDay] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [endYear, setEndYear] = useState('');
  const [selectedJournal, setSelectedJournal] = useState(null)
  const [journalDetails, setJournalDetails] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showDetailsInTable, setShowDetailsInTable] = useState(false)
  const [allJournalDetails, setAllJournalDetails] = useState({})
  const [coopProfile, setCoopProfile] = useState(null)
  const [branchInfo, setBranchInfo] = useState(null)
  const [logoBase64, setLogoBase64] = useState('')
  const { trustees } = useTrusteesFooter(startDate, endDate)

  useEffect(() => {
    // Load logo as base64
    const loadLogo = async () => {
      try {
        const response = await fetch('/logo.jpeg')
        const blob = await response.blob()
        const reader = new FileReader()
        reader.onloadend = () => setLogoBase64(reader.result)
        reader.readAsDataURL(blob)
      } catch (e) { console.log('Failed to load logo') }
    }
    loadLogo()
  }, [])

  useEffect(() => {
    // Set default dates: today to today
    const today = new Date()
    const y = today.getFullYear()
    const m = String(today.getMonth() + 1).padStart(2, '0')
    const d = String(today.getDate()).padStart(2, '0')
    setStartYear(String(y))
    setStartMonth(m)
    setStartDay(d)
    setEndYear(String(y))
    setEndMonth(m)
    setEndDay(d)
  }, [])

  // Compose date strings from dropdown parts
  useEffect(() => {
    if (startYear && startMonth && startDay) {
      setStartDate(`${startYear}-${startMonth}-${startDay}`)
    }
  }, [startYear, startMonth, startDay])

  useEffect(() => {
    if (endYear && endMonth && endDay) {
      setEndDate(`${endYear}-${endMonth}-${endDay}`)
    }
  }, [endYear, endMonth, endDay])

  useEffect(() => {
    // Fetch cooperative profile and branch info
    const fetchProfileInfo = async () => {
      try {
        const response = await api.get('/reports/profile-info')
        setCoopProfile(response.data.data.coopProfile)
        setBranchInfo(response.data.data.branchInfo)
      } catch (error) {
        console.error('Error fetching profile info:', error)
      }
    }
    fetchProfileInfo()
  }, [])

  useEffect(() => {
    if (startDate && endDate) {
      fetchJournals()
    }
  }, [startDate, endDate])

  const fetchJournals = async () => {
    try {
      setLoading(true)
      const response = await api.get('/reports/jurnal', {
        params: { startDate, endDate }
      })
      setJournals(response.data.data || [])
      
      // Fetch details for all journals if showDetailsInTable is true
      if (showDetailsInTable && response.data.data && response.data.data.length > 0) {
        await fetchAllJournalDetails(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching journals:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchAllJournalDetails = async (journalList) => {
    try {
      const detailsMap = {}
      for (const journal of journalList) {
        try {
          const response = await api.get(`/reports/jurnal/${journal.NoJurnal}/detail`)
          detailsMap[journal.NoJurnal] = response.data.data
        } catch (error) {
          console.error(`Error fetching details for ${journal.NoJurnal}:`, error)
        }
      }
      setAllJournalDetails(detailsMap)
    } catch (error) {
      console.error('Error fetching all journal details:', error)
    }
  }

  const formatCurrency = (value) => {
    if (!value) return 'Rp 0'
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value)
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('id-ID')
  }

  const handleViewDetail = async (journal) => {
    try {
      setSelectedJournal(journal)
      setShowDetailModal(true)
      const response = await api.get(`/reports/jurnal/${journal.NoJurnal}/detail`)
      setJournalDetails(response.data.data)
    } catch (error) {
      console.error('Error fetching journal details:', error)
    }
  }

  const handlePrint = async () => {
    // Fetch all details if not already loaded
    if (showDetailsInTable && Object.keys(allJournalDetails).length === 0) {
      await fetchAllJournalDetails(journals)
    }

    const printWindow = window.open('', '_blank')
    printWindow.document.write(`
      <html>
      <head>
        <title>Laporan Jurnal Umum</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            margin: 20px;
            padding: 10px;
          }
          .kop-surat { text-align: center; border-bottom: 3px solid #000; padding-bottom: 15px; margin-bottom: 20px; }
          .kop-logo { width: 70px; height: auto; }
          .kop-nama { font-size: 20px; font-weight: bold; margin: 5px 0; }
          .kop-badan-hukum { font-size: 12px; margin: 3px 0; }
          .kop-alamat { font-size: 11px; margin: 2px 0; }
          .kop-kontak { font-size: 10px; margin: 2px 0; }
          .header {
            margin-bottom: 20px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          th, td {
            border: 1px solid #000;
            padding: 8px;
            text-align: left;
            font-size: 11px;
          }
          th {
            background-color: #f0f0f0;
            font-weight: bold;
            text-align: center;
          }
          .text-right {
            text-align: right;
          }
          .text-center {
            text-align: center;
          }
          .journal-header {
            background-color: #e8f4f8;
            font-weight: bold;
          }
          .detail-table {
            margin: 10px 0;
            margin-left: 20px;
            width: 95%;
          }
          @media print {
            body {
              margin: 0;
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="kop-surat">
            <img src="${window.location.origin}/logo.jpeg" class="kop-logo" alt="Logo" />
            <div class="kop-nama">${coopProfile?.NamaKoperasi || 'KOPERASI SERBA USAHA'}</div>
            <div class="kop-badan-hukum">Badan Hukum No. : ${coopProfile?.NoBadanHukum || '-'}</div>
            <div class="kop-alamat">${coopProfile?.Alamat || ''}</div>
            <div class="kop-kontak">Telp: ${coopProfile?.Telepon || '-'}  |  Email: ${coopProfile?.Email || '-'}  |  NPWP: ${coopProfile?.NPWP || '-'}</div>
          </div>
        
        <h2>Laporan Jurnal Umum</h2>
        
        <div class="info">
          <div class="info-row">
            <span class="info-label">Periode:</span>
            <span>${formatDate(startDate)} s/d ${formatDate(endDate)}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Cabang:</span>
            <span>${branchInfo?.NamaCabang || user?.cabangId || '-'}</span>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>No Jurnal</th>
              <th>Tanggal</th>
              <th>No Referensi</th>
              <th>Keterangan</th>
              <th class="text-right">Total Debet</th>
              <th class="text-right">Total Kredit</th>
            </tr>
          </thead>
          <tbody>
            ${journals.map(j => `
              <tr class="journal-header">
                <td>${j.NoJurnal}</td>
                <td>${formatDate(j.TglJurnal)}</td>
                <td>${j.NoReferensi}</td>
                <td>${j.Keterangan}</td>
                <td class="text-right">${formatCurrency(j.TotalDebet)}</td>
                <td class="text-right">${formatCurrency(j.TotalKredit)}</td>
              </tr>
              ${allJournalDetails[j.NoJurnal] && allJournalDetails[j.NoJurnal].details ? `
                <tr>
                  <td colspan="6" style="padding: 0;">
                    <table class="detail-table">
                      <thead>
                        <tr>
                          <th>Kode Akun</th>
                          <th>Nama Akun</th>
                          <th>Keterangan</th>
                          <th class="text-right">Debet</th>
                          <th class="text-right">Kredit</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${allJournalDetails[j.NoJurnal].details.map(d => `
                          <tr>
                            <td>${d.KodeAkun}</td>
                            <td>${d.NamaAkun || '-'}</td>
                            <td>${d.Keterangan}</td>
                            <td class="text-right">${formatCurrency(d.Debet)}</td>
                            <td class="text-right">${formatCurrency(d.Kredit)}</td>
                          </tr>
                        `).join('')}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colspan="3" class="text-right"><strong>Total</strong></td>
                          <td class="text-right"><strong>${formatCurrency(allJournalDetails[j.NoJurnal].totalDebet)}</strong></td>
                          <td class="text-right"><strong>${formatCurrency(allJournalDetails[j.NoJurnal].totalKredit)}</strong></td>
                        </tr>
                      </tfoot>
                    </table>
                  </td>
                </tr>
              ` : ''}
            `).join('')}
          </tbody>
        </table>
        
        <div class="info">
          <div class="info-row">
            <span class="info-label">Total Debet:</span>
            <span>${formatCurrency(journals.reduce((sum, j) => sum + (parseFloat(j.TotalDebet) || 0), 0))}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Total Kredit:</span>
            <span>${formatCurrency(journals.reduce((sum, j) => sum + (parseFloat(j.TotalKredit) || 0), 0))}</span>
          </div>
        </div>
        
        <div style="margin-top: 30px; text-align: right;">
          <p>Dicetak pada: ${new Date().toLocaleString('id-ID')}</p>
        </div>

        ${renderTrusteesHtml(trustees)}
      </body>
      </html>
    `)
    printWindow.document.close()
    printWindow.print()
  }

  const handleCloseModal = () => {
    setShowDetailModal(false)
    setSelectedJournal(null)
    setJournalDetails(null)
  }

  const handleExportExcel = async () => {
    if (showDetailsInTable && Object.keys(allJournalDetails).length === 0) {
      await fetchAllJournalDetails(journals)
    }

    const wb = XLSX.utils.book_new()
    
    // Header file Excel
    let wsData = [
      [coopProfile?.NamaKoperasi || 'Koperasi Simpan Pinjam'],
      ['LAPORAN JURNAL UMUM'],
      [`Periode: ${formatDate(startDate)} s/d ${formatDate(endDate)}`],
      [`Cabang: ${branchInfo?.NamaCabang || '-'}`],
      [], // Baris kosong
      ['No Jurnal', 'Tanggal', 'No Referensi', 'Keterangan', 'Debet', 'Kredit']
    ]

    journals.forEach(j => {
      // Data Jurnal
      wsData.push([
        j.NoJurnal,
        formatDate(j.TglJurnal),
        j.NoReferensi,
        j.Keterangan,
        parseFloat(j.TotalDebet) || 0,
        parseFloat(j.TotalKredit) || 0
      ])

      // Data Detail (Jika tampil detail)
      const details = allJournalDetails[j.NoJurnal]
      if (showDetailsInTable && details && details.details) {
        details.details.forEach(d => {
          wsData.push([
            d.KodeAkun,
            d.NamaAkun,
            '',
            d.Keterangan,
            parseFloat(d.Debet) || 0,
            parseFloat(d.Kredit) || 0
          ])
        })
      }
    })

    const ws = XLSX.utils.aoa_to_sheet(wsData)
    
    // Auto-width kolom
    ws['!cols'] = [
      { wch: 18 }, { wch: 15 }, { wch: 20 }, { wch: 45 }, { wch: 15 }, { wch: 15 }
    ]

    XLSX.utils.book_append_sheet(wb, ws, 'Jurnal Umum')
    XLSX.writeFile(wb, `Jurnal_Umum_${startDate}_sd_${endDate}.xlsx`)
  }

  const handleExportPDF = async () => {
    if (showDetailsInTable && Object.keys(allJournalDetails).length === 0) {
      await fetchAllJournalDetails(journals)
    }

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    })

    const pageWidth = doc.internal.pageSize.width

    const logoUrl = window.location.origin + '/logo.jpeg'
    try {
      doc.addImage(logoUrl, 'JPEG', 14, 5, 20, 20)
    } catch (e) {
      console.log('Logo PDF failed:', e.message)
    }

    // Kop Surat Header
    doc.setFontSize(16).setFont(undefined, 'bold')
    doc.text(coopProfile?.NamaKoperasi || 'KOPERASI SERBA USAHA', pageWidth / 2, 10, { align: 'center' })
    doc.setFontSize(9).setFont(undefined, 'normal')
    doc.text(`Badan Hukum No. : ${coopProfile?.NoBadanHukum || '-'}`, pageWidth / 2, 15, { align: 'center' })
    doc.setFontSize(8).text(coopProfile?.Alamat || '', pageWidth / 2, 20, { align: 'center' })
    doc.text(`Telp: ${coopProfile?.Telepon || '-'}  |  Email: ${coopProfile?.Email || '-'}  |  Website: ${coopProfile?.Website || '-'}  |  NPWP: ${coopProfile?.NPWP || '-'}`, pageWidth / 2, 25, { align: 'center' })

    // Garis pemisah
    doc.setLineWidth(0.5)
    doc.line(10, 28, pageWidth - 10, 28)
    doc.setLineWidth(0.3)
    doc.line(10, 29, pageWidth - 10, 29)

    // Judul Laporan
    doc.setFontSize(11).setFont(undefined, 'bold')
    doc.text('LAPORAN JURNAL UMUM', pageWidth / 2, 35, { align: 'center' })
    doc.setFontSize(8).setFont(undefined, 'normal')
    doc.text(`Periode: ${formatDate(startDate)} s/d ${formatDate(endDate)} | Cabang: ${branchInfo?.NamaCabang || '-'}`, pageWidth / 2, 40, { align: 'center' })

    // Definisikan Header Tabel
    const tableHeaders = [['No Jurnal / Kode Akun', 'Tanggal / Nama Akun', 'No Referensi', 'Keterangan', 'Debet', 'Kredit']]
    
    // Mapping Data agar rapi (Menyelesaikan masalah header berantakan)
    let tableData = []
    journals.forEach(j => {
      // Baris Utama Jurnal (Warna Biru Muda)
      tableData.push([
        { content: j.NoJurnal, styles: { fontStyle: 'bold', fillColor: [232, 244, 248] } },
        { content: formatDate(j.TglJurnal), styles: { fontStyle: 'bold', fillColor: [232, 244, 248] } },
        { content: j.NoReferensi, styles: { fontStyle: 'bold', fillColor: [232, 244, 248] } },
        { content: j.Keterangan, styles: { fontStyle: 'bold', fillColor: [232, 244, 248] } },
        { content: formatCurrency(j.TotalDebet), styles: { fontStyle: 'bold', halign: 'right', fillColor: [232, 244, 248] } },
        { content: formatCurrency(j.TotalKredit), styles: { fontStyle: 'bold', halign: 'right', fillColor: [232, 244, 248] } }
      ])

      // Baris Detail Transaksi (Jika diaktifkan)
      const details = allJournalDetails[j.NoJurnal]
      if (showDetailsInTable && details && details.details) {
        details.details.forEach(d => {
          tableData.push([
            `   ${d.KodeAkun}`, // Spasi untuk identasi
            d.NamaAkun || '-',
            '',
            d.Keterangan,
            { content: formatCurrency(d.Debet), styles: { halign: 'right' } },
            { content: formatCurrency(d.Kredit), styles: { halign: 'right' } }
          ])
        })
      }
    })

    autoTable(doc, {
      head: tableHeaders,
      body: tableData,
      startY: 45,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
      headStyles: { fillColor: [68, 114, 196], textColor: 255, halign: 'center' },
      columnStyles: {
        4: { halign: 'right', minCellWidth: 25 },
        5: { halign: 'right', minCellWidth: 25 }
      },
      margin: { left: 10, right: 10 },
      didDrawPage: (data) => {
        doc.setFontSize(7)
        doc.text(`Halaman ${data.pageNumber}`, pageWidth - 25, doc.internal.pageSize.height - 10)
      }
    })

    // Footer trustees
    if (trustees.length > 0) {
      doc.setFontSize(6)
      const midX = pageWidth / 2
      
      const pengurus = trustees.filter(t => t.Kelompok === 'Pengurus')
      const pengawas = trustees.filter(t => t.Kelompok === 'Pengawas')
      const pengurusSub = pengurus.filter(t => t.Jabatan === 'Anggota/Panitia Kredit')
      const pengurusRest = pengurus.filter(t => t.Jabatan !== 'Anggota/Panitia Kredit')

      let yL = doc.lastAutoTable.finalY + 8
      if (pengurus.length) {
        doc.setFont(undefined, 'bold')
        doc.text('PENGURUS:', 14, yL)
        yL += 3
        doc.setFont(undefined, 'normal')
        pengurusRest.forEach(t => {
          doc.text(`${t.Jabatan} : ${t.NamaLengkap}`, 19, yL)
          yL += 3
        })
        if (pengurusSub.length) {
          doc.text('Anggota/Panitia Kredit :', 19, yL)
          yL += 3
          pengurusSub.forEach((t, i) => {
            doc.text(`${i + 1}. ${t.NamaLengkap}`, 24, yL)
            yL += 3
          })
        }
      }

      let yR = doc.lastAutoTable.finalY + 8
      if (pengawas.length) {
        doc.setFont(undefined, 'bold')
        doc.text('PENGAWAS:', midX + 5, yR)
        yR += 3
        doc.setFont(undefined, 'normal')
        pengawas.forEach(t => {
          doc.text(`${t.Jabatan} : ${t.NamaLengkap}`, midX + 10, yR)
          yR += 3
        })
      }
    }

    doc.save(`Jurnal_${startDate}_${endDate}.pdf`)
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Laporan Jurnal Umum</h3>
          <div className="card-tools">
            <button
              className="btn btn-sm btn-success"
              onClick={handleExportExcel}
              disabled={journals.length === 0}
            >
              <i className="fas fa-file-excel mr-2"></i>
              Excel
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={handleExportPDF}
              disabled={journals.length === 0}
            >
              <i className="fas fa-file-pdf mr-2"></i>
              PDF
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={handlePrint}
              disabled={journals.length === 0}
            >
              <i className="fas fa-print mr-2"></i>
              Cetak
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="row mb-4 bg-light p-3 rounded align-items-end">
            <div className="col-md-4">
              <label className="small font-weight-bold">TANGGAL MULAI</label>
              <div className="d-flex">
                <select className="form-control form-control-sm mr-2" style={{ width: '70px' }} value={startDay} onChange={(e) => setStartDay(e.target.value)}>
                  {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>)}
                </select>
                <select className="form-control form-control-sm mr-2" style={{ width: 'auto' }} value={startMonth} onChange={(e) => setStartMonth(e.target.value)}>
                  <option value="01">Januari</option><option value="02">Februari</option><option value="03">Maret</option>
                  <option value="04">April</option><option value="05">Mei</option><option value="06">Juni</option>
                  <option value="07">Juli</option><option value="08">Agustus</option><option value="09">September</option>
                  <option value="10">Oktober</option><option value="11">November</option><option value="12">Desember</option>
                </select>
                <select className="form-control form-control-sm" style={{ width: '100px' }} value={startYear} onChange={(e) => setStartYear(e.target.value)}>
                  {Array.from({ length: 10 }, (_, i) => <option key={i} value={String(new Date().getFullYear() - 5 + i)}>{new Date().getFullYear() - 5 + i}</option>)}
                </select>
              </div>
            </div>
            <div className="col-md-4">
              <label className="small font-weight-bold">TANGGAL AKHIR</label>
              <div className="d-flex">
                <select className="form-control form-control-sm mr-2" style={{ width: '70px' }} value={endDay} onChange={(e) => setEndDay(e.target.value)}>
                  {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>)}
                </select>
                <select className="form-control form-control-sm mr-2" style={{ width: 'auto' }} value={endMonth} onChange={(e) => setEndMonth(e.target.value)}>
                  <option value="01">Januari</option><option value="02">Februari</option><option value="03">Maret</option>
                  <option value="04">April</option><option value="05">Mei</option><option value="06">Juni</option>
                  <option value="07">Juli</option><option value="08">Agustus</option><option value="09">September</option>
                  <option value="10">Oktober</option><option value="11">November</option><option value="12">Desember</option>
                </select>
                <select className="form-control form-control-sm" style={{ width: '100px' }} value={endYear} onChange={(e) => setEndYear(e.target.value)}>
                  {Array.from({ length: 10 }, (_, i) => <option key={i} value={String(new Date().getFullYear() - 5 + i)}>{new Date().getFullYear() - 5 + i}</option>)}
                </select>
              </div>
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <button className="btn btn-primary mr-2" onClick={fetchJournals} disabled={loading}>
                <i className="fas fa-search mr-2"></i>
                Tampilkan
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDetailsInTable(!showDetailsInTable)
                  if (!showDetailsInTable && journals.length > 0) {
                    fetchAllJournalDetails(journals)
                  }
                }}
                disabled={loading || journals.length === 0}
              >
                <i className="fas fa-list mr-2"></i>
                {showDetailsInTable ? 'Sembunyikan Detail' : 'Tampilkan Detail'}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th>No Jurnal</th>
                    <th>Tanggal</th>
                    <th>No Referensi</th>
                    <th>Keterangan</th>
                    <th className="text-right">Total Debet</th>
                    <th className="text-right">Total Kredit</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {journals.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        Tidak ada data jurnal
                      </td>
                    </tr>
                  ) : (
                    journals.map((journal) => (
                      <React.Fragment key={journal.NoJurnal}>
                        <tr>
                          <td>{journal.NoJurnal}</td>
                          <td>{formatDate(journal.TglJurnal)}</td>
                          <td>{journal.NoReferensi}</td>
                          <td>{journal.Keterangan}</td>
                          <td className="text-right">{formatCurrency(journal.TotalDebet)}</td>
                          <td className="text-right">{formatCurrency(journal.TotalKredit)}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => handleViewDetail(journal)}
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                          </td>
                        </tr>
                        {showDetailsInTable && allJournalDetails[journal.NoJurnal] && (
                          <tr>
                            <td colSpan="7" style={{ backgroundColor: '#f8f9fa' }}>
                              <table className="table table-sm mb-0">
                                <thead>
                                  <tr>
                                    <th>Kode Akun</th>
                                    <th>Nama Akun</th>
                                    <th>Keterangan</th>
                                    <th className="text-right">Debet</th>
                                    <th className="text-right">Kredit</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {allJournalDetails[journal.NoJurnal].details.map((detail, idx) => (
                                    <tr key={idx}>
                                      <td>{detail.KodeAkun}</td>
                                      <td>{detail.NamaAkun || '-'}</td>
                                      <td>{detail.Keterangan}</td>
                                      <td className="text-right">{formatCurrency(detail.Debet)}</td>
                                      <td className="text-right">{formatCurrency(detail.Kredit)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Journal Detail Modal */}
      {showDetailModal && selectedJournal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Detail Jurnal: {selectedJournal.NoJurnal}</h5>
                <button type="button" className="close" onClick={handleCloseModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Tanggal:</strong> {formatDate(selectedJournal.TglJurnal)}
                  </div>
                  <div className="col-md-6">
                    <strong>No Referensi:</strong> {selectedJournal.NoReferensi}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-12">
                    <strong>Keterangan:</strong> {selectedJournal.Keterangan}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Total Debet:</strong> {formatCurrency(selectedJournal.TotalDebet)}
                  </div>
                  <div className="col-md-6">
                    <strong>Total Kredit:</strong> {formatCurrency(selectedJournal.TotalKredit)}
                  </div>
                </div>
                <hr />
                <h6>Detail Transaksi</h6>
                {journalDetails ? (
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <thead>
                        <tr>
                          <th>Kode Akun</th>
                          <th>Nama Akun</th>
                          <th>Keterangan</th>
                          <th className="text-right">Debet</th>
                          <th className="text-right">Kredit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {journalDetails.details && journalDetails.details.length > 0 ? (
                          journalDetails.details.map((detail, index) => (
                            <tr key={index}>
                              <td>{detail.KodeAkun}</td>
                              <td>{detail.NamaAkun || '-'}</td>
                              <td>{detail.Keterangan}</td>
                              <td className="text-right">{formatCurrency(detail.Debet)}</td>
                              <td className="text-right">{formatCurrency(detail.Kredit)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center">
                              Tidak ada detail transaksi
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="3" className="text-right"><strong>Total</strong></td>
                          <td className="text-right"><strong>{formatCurrency(journalDetails.totalDebet)}</strong></td>
                          <td className="text-right"><strong>{formatCurrency(journalDetails.totalKredit)}</strong></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default JurnalUmum
